import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Award, Download, Share2, Home } from 'lucide-react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

export default function CursoCertificado() {
  const navigate = useNavigate();
  const [userName, setUserName] = useState('');
  const [courseTitle, setCourseTitle] = useState('Fundamentos Bíblicos');
  const [completionDate, setCompletionDate] = useState('');
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    // Verificar se o usuário completou a avaliação
    const checkAssessmentStatus = async () => {
      try {
        const savedResults = localStorage.getItem('courseAssessmentResults');
        if (!savedResults) {
          alert('Você precisa concluir a avaliação final para acessar o certificado.');
          navigate('/cursos/avaliacao');
          return;
        }

        const { passed, date } = JSON.parse(savedResults);
        if (!passed) {
          alert('Você precisa ser aprovado na avaliação final para receber o certificado.');
          navigate('/cursos/avaliacao');
          return;
        }

        // Carregar data de conclusão
        if (date) {
          const formattedDate = new Date(date).toLocaleDateString('pt-BR');
          setCompletionDate(formattedDate);
        } else {
          setCompletionDate(new Date().toLocaleDateString('pt-BR'));
        }

        // Carregar nome do usuário
        const userData = localStorage.getItem('userData');
        if (userData) {
          const { name } = JSON.parse(userData);
          setUserName(name || 'Aluno da Plataforma');
        } else {
          setUserName('Aluno da Plataforma');
        }

        setLoading(false);
      } catch (error) {
        console.error('Error checking assessment status:', error);
        setLoading(false);
      }
    };

    checkAssessmentStatus();
  }, [navigate]);

  const generatePDF = async () => {
    try {
      setGenerating(true);
      
      const certificateElement = document.getElementById('certificate');
      if (!certificateElement) {
        throw new Error('Certificate element not found');
      }
      
      const canvas = await html2canvas(certificateElement, {
        scale: 2,
        useCORS: true,
        logging: false
      });
      
      const imgData = canvas.toDataURL('image/jpeg', 1.0);
      
      // A4 dimensions: 210 x 297 mm
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      
      // Calculate ratio to fit the image properly
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);
      
      const imgX = (pdfWidth - imgWidth * ratio) / 2;
      const imgY = (pdfHeight - imgHeight * ratio) / 2;
      
      pdf.addImage(imgData, 'JPEG', imgX, imgY, imgWidth * ratio, imgHeight * ratio);
      
      // Save the PDF
      pdf.save(`Certificado_${courseTitle.replace(/\s+/g, '_')}.pdf`);
      
      setGenerating(false);
    } catch (error) {
      console.error('Error generating certificate:', error);
      alert('Ocorreu um erro ao gerar o certificado. Por favor, tente novamente.');
      setGenerating(false);
    }
  };

  const shareCertificate = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: 'Meu Certificado',
          text: `Concluí o curso ${courseTitle} na plataforma SdT BSB!`,
          // url: window.location.href
        });
      } else {
        alert('Compartilhamento não suportado neste navegador. Por favor, baixe o certificado e compartilhe manualmente.');
      }
    } catch (error) {
      console.error('Error sharing certificate:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-teal"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-3">
          <Award className="h-8 w-8 text-brand-teal" />
          <h1 className="text-3xl font-bold text-brand-teal">Certificado de Conclusão</h1>
        </div>
        <button
          onClick={() => navigate('/cursos')}
          className="flex items-center gap-2 px-4 py-2 bg-brand-teal text-white rounded-lg hover:bg-brand-teal/90 transition-colors"
        >
          <Home className="h-4 w-4" />
          <span>Voltar para Cursos</span>
        </button>
      </div>

      {/* Certificado */}
      <div className="mb-8 bg-white p-4 rounded-xl shadow-elevation-1">
        <div 
          id="certificate" 
          className="border-[20px] border-brand-teal p-8 rounded-lg bg-white"
          style={{ aspectRatio: '1/1.414' }} // A4 aspect ratio
        >
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-brand-teal mb-2">Certificado</h2>
            <p className="text-xl text-gray-700">de Conclusão</p>
          </div>
          
          <div className="text-center mb-8">
            <p className="text-lg text-gray-700">Certificamos que</p>
            <p className="text-3xl font-bold text-gray-900 my-6 border-b-2 border-brand-teal inline-block px-8 pb-2">{userName}</p>
            <p className="text-lg text-gray-700">
              concluiu com êxito o curso
            </p>
            <p className="text-2xl font-bold text-brand-teal my-4">{courseTitle}</p>
            <p className="text-lg text-gray-700">
              demonstrando dedicação, conhecimento e excelência acadêmica.
            </p>
          </div>
          
          <div className="text-center mb-12">
            <p className="text-lg text-gray-700">
              Concluído em {completionDate}
            </p>
          </div>
          
          <div className="flex justify-around mb-8">
            <div className="text-center">
              <div className="w-40 border-t border-gray-400 mx-auto"></div>
              <p className="mt-2 text-gray-600">Diretor do Curso</p>
            </div>
            <div className="text-center">
              <div className="w-40 border-t border-gray-400 mx-auto"></div>
              <p className="mt-2 text-gray-600">Coordenador Acadêmico</p>
            </div>
          </div>
          
          <div className="text-center text-sm text-gray-500">
            <p>Este certificado verifica a conclusão do curso online e não constitui um diploma ou grau acadêmico.</p>
          </div>
          
          <div className="absolute bottom-8 right-8 w-24 h-24 bg-brand-teal rounded-full flex items-center justify-center text-white font-bold transform -rotate-15 border-4 border-brand-teal/70">
            <div className="text-center">
              <p>CERTIFICADO</p>
              <p>OFICIAL</p>
            </div>
          </div>
        </div>
      </div>

      {/* Botões de ação */}
      <div className="flex flex-col sm:flex-row gap-4">
        <button
          onClick={generatePDF}
          disabled={generating}
          className="flex-1 bg-brand-teal text-white px-6 py-3 rounded-lg hover:bg-brand-teal/90 transition-colors flex items-center justify-center gap-2 disabled:opacity-70"
        >
          {generating ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
              <span>Gerando PDF...</span>
            </>
          ) : (
            <>
              <Download className="h-5 w-5" />
              <span>Baixar Certificado</span>
            </>
          )}
        </button>
        
        <button
          onClick={shareCertificate}
          className="flex-1 border border-brand-teal text-brand-teal px-6 py-3 rounded-lg hover:bg-brand-light transition-colors flex items-center justify-center gap-2"
        >
          <Share2 className="h-5 w-5" />
          <span>Compartilhar</span>
        </button>
      </div>
    </div>
  );
}